import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { ScanOptionsPanel } from '../../components/ScanOptionsPanel';
import { toast } from 'sonner';
import { useBatchImportStatus } from '../../hooks/useBatchImportStatus';
import { BatchImportStatus } from '../../components/BatchImportStatus';
import {
  API_URL, PIPELINE_API, authedFetch,
} from './cdm/api';
import {
  getLangDisplay, STATUS_COLORS, STATUS_ICONS, DOC_TYPE_COLORS,
  NODE_TYPE_COLORS, CHUNK_TYPE_COLORS, formatDate, truncateText,
  SUPPORTED_EXTENSIONS,
} from './cdm/constants';
import { DocumentDetailDialog } from './cdm/DocumentDetailDialog';
import { UploadDocumentDialog } from './cdm/UploadDocumentDialog';
import { HelpGuideDialog } from './cdm/HelpGuideDialog';
import { ProviderHealthPanel } from './cdm/ProviderHealthPanel';
import { PipelineConfigCard } from './cdm/PipelineConfigCard';
import { LangDetectModelPicker, ModelCombobox } from './cdm/ModelPickers';
import { HelpSection } from './cdm/HelpSection';

import {
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  Eye,
  Trash2,
  Database,
  Layers,
  Box,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  XCircle,
  BarChart3,
  PieChart,
  Activity,
  Upload,
  X,
  BookOpen,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Zap,
  Server,
  Globe,
  Link2,
  Brain,
  Sparkles,
  Save,
  ChevronsUpDown,
  AlertTriangle
} from 'lucide-react';
import { Settings } from 'lucide-react';



export default function CDMDocumentsSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Overview stats
  const [systemStats, setSystemStats] = useState(null);
  
  // Documents list
  const [documents, setDocuments] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [page, setPage] = useState(() => parseInt(searchParams.get('page')) || 1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'all');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [parentFilter, setParentFilter] = useState(() => searchParams.get('parent_filter') || 'all');
  const [reviewFilter, setReviewFilter] = useState(() => searchParams.get('review_filter') || 'all');
  const [embeddingFilter, setEmbeddingFilter] = useState(() => searchParams.get('embedding_filter') || 'all');
  const [reembeddingDocIds, setReembeddingDocIds] = useState([]);
  const [bulkReembedProgress, setBulkReembedProgress] = useState(null);
  const [collectionFilter, setCollectionFilter] = useState(() => searchParams.get('collection') || '');
  const [collapsedParents, setCollapsedParents] = useState(new Set());
  const [editingChunk, setEditingChunk] = useState(null);
  const [editingChunkText, setEditingChunkText] = useState('');
  const [savingChunk, setSavingChunk] = useState(false);
  const [chunkSaveSuccess, setChunkSaveSuccess] = useState(null);
  const [editingDocSummary, setEditingDocSummary] = useState(false);
  const [editingDocSummaryText, setEditingDocSummaryText] = useState('');
  const [savingDocSummary, setSavingDocSummary] = useState(false);
  const [editingSectionIdx, setEditingSectionIdx] = useState(null);
  const [editingSectionText, setEditingSectionText] = useState('');
  const [editingSectionTitle, setEditingSectionTitle] = useState('');
  const [savingSectionIdx, setSavingSectionIdx] = useState(null);
  const [sectionSaveSuccess, setSectionSaveSuccess] = useState(null);
  
  // Selected document detail
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docDetail, setDocDetail] = useState(null);
  const [docStats, setDocStats] = useState(null);
  const [docNodes, setDocNodes] = useState([]);

  // RAG Rules
  const [ragRules, setRagRules] = useState("");
  const [ragRulesOpen, setRagRulesOpen] = useState(false);
  const [savingRules, setSavingRules] = useState(false);

  // System Behavior Rules
  const [systemRules, setSystemRules] = useState("");
  const [systemRulesOpen, setSystemRulesOpen] = useState(false);
  const [savingSystemRules, setSavingSystemRules] = useState(false);
  const [docChunks, setDocChunks] = useState([]);
  const [nodesPage, setNodesPage] = useState(1);
  const [chunksPage, setChunksPage] = useState(1);
  const [totalNodes, setTotalNodes] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Upload replace mode (Scenario 3b)
  const [replaceMode, setReplaceMode] = useState(false);
  const [replaceTargetDoc, setReplaceTargetDoc] = useState(null);
  const [replaceSearchOpen, setReplaceSearchOpen] = useState(false);
  const [parentDocsForReplace, setParentDocsForReplace] = useState([]);

  // Upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);  // Array of {file, status, error}
  const [uploadCollectionId, setUploadCollectionId] = useState('General');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, succeeded: 0, failed: 0 });
  const [uploadCancelled, setUploadCancelled] = useState(false);
  const uploadCancelledRef = React.useRef(false);

  // Collection state
  const [collections, setCollections] = useState(['General']);
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');

  // Re-index state
  const [reindexing, setReindexing] = useState(false);

  // Process all pending state
  const [processingAll, setProcessingAll] = useState(false);

  // Enrichment progress state (RAG V5)
  const [enrichmentProgress, setEnrichmentProgress] = useState(null);

  // System health & capacity state (Level 6 + Level 7)
  const [systemHealth, setSystemHealth] = useState(null);
  const [systemCapacity, setSystemCapacity] = useState(null);

  // Help guide state
  const [showHelpGuide, setShowHelpGuide] = useState(false);
  const [showPipelineConfig, setShowPipelineConfig] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  // AI Detection settings state
  const [detectionSettings, setDetectionSettings] = useState({
    enabled: false,
    models: [null, null, null, null],
    prompt: '',
  });
  const [detectionSettingsOpen, setDetectionSettingsOpen] = useState(false);
  const [savingDetection, setSavingDetection] = useState(false);
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [aiDetectionResult, setAiDetectionResult] = useState(null);
  const [tensornexStatus, setTensornexStatus] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [graphStatusBulk, setGraphStatusBulk] = useState({});
  const [cacheTtlDays, setCacheTtlDays] = useState(7);
  const [savingCacheTtl, setSavingCacheTtl] = useState(false);
  const [showCacheList, setShowCacheList] = useState(false);
  const [cacheEntries, setCacheEntries] = useState([]);
  const [loadingCacheList, setLoadingCacheList] = useState(false);
  const [docGraphStatus, setDocGraphStatus] = useState(null);

  // Summary settings state
  const [summarySettings, setSummarySettings] = useState({ enabled: false, model: null, doc_prompt: '', section_prompt: '' });
  const [summarySettingsOpen, setSummarySettingsOpen] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState(null);
  const [docSummaryStatus, setDocSummaryStatus] = useState(null);
  const [generatingAllSummaries, setGeneratingAllSummaries] = useState(false);
  const [summaryBulkStatus, setSummaryBulkStatus] = useState(null);
  const [summaryJobPolling, setSummaryJobPolling] = useState(false);
  const [summaryDocSearch, setSummaryDocSearch] = useState('');

  // KB Article Import state
  const [kbImportOpen, setKbImportOpen] = useState(false);
  const [kbCategories, setKbCategories] = useState([]);
  const [kbSelectedCats, setKbSelectedCats] = useState([]);
  const [kbExpanded, setKbExpanded] = useState([]);
  const [kbArticlesByCat, setKbArticlesByCat] = useState({});
  const [kbLoadingArts, setKbLoadingArts] = useState([]);
  const [kbSelectedArticles, setKbSelectedArticles] = useState([]);
  const [kbCatSearch, setKbCatSearch] = useState('');
  const [kbImportStatus, setKbImportStatus] = useState(null);
  const [kbImportPolling, setKbImportPolling] = useState(false);
  const [loadingKbCats, setLoadingKbCats] = useState(false);

  // URL Import state
  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` };
  const { activeBatches, batchDetails, isPolling, startPolling, refreshActive, cancelAll } = useBatchImportStatus(authHeaders);

  // On page load/refresh: detect any imports already running and show + poll them
  useEffect(() => {
    (async () => {
      const hasActive = await refreshActive();
      if (hasActive) startPolling();
    })();
  }, []);

  const handleCancelAllImports = async () => {
    const res = await cancelAll();
    if (res) {
      const kbNote = res.kb_import_cancelled ? ' + KB import' : '';
      toast.success(`Cancelled ${res.cancelled_tasks} import task(s)${kbNote}`);
      setTimeout(() => { refreshActive(); fetchDocuments(); }, 1500);
    } else {
      toast.error('Failed to cancel imports');
    }
  };
  const [urlImportOpen, setUrlImportOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [scannedPages, setScannedPages] = useState([]);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [scanningUrl, setScanningUrl] = useState(false);
  const [urlImportStatus, setUrlImportStatus] = useState(null);
  const [urlCollectionName, setUrlCollectionName] = useState('URLs');
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [bulkType, setBulkType] = useState('');
  const [bulkCollection, setBulkCollection] = useState('');
  const [orphanData, setOrphanData] = useState(null);
  const [checkingOrphans, setCheckingOrphans] = useState(false);
  const [deletingOrphans, setDeletingOrphans] = useState(false);

  // Scan Options state (per-dialog localStorage + MongoDB defaults)
  const SCAN_LS_KEYS = { url: 'scan_opts_url', upload: 'scan_opts_upload', kb: 'scan_opts_kb' };
  const [scanOptsUrl, setScanOptsUrl] = useState(null);
  const [scanOptsUpload, setScanOptsUpload] = useState(null);
  const [scanOptsKb, setScanOptsKb] = useState(null);
  const [scanOptsDefaults, setScanOptsDefaults] = useState(null);
  const [scanOptsDefaultsLoading, setScanOptsDefaultsLoading] = useState(false);

  // Language Auto-Detection Settings state (Phase 6.5)
  const [langDetectSettings, setLangDetectSettings] = useState(null);
  const [langDetectSettingsLoading, setLangDetectSettingsLoading] = useState(false);
  const [savingLangDetect, setSavingLangDetect] = useState(false);

  // Load scan options for a dialog: localStorage first, then MongoDB defaults
  const loadScanOptsForDialog = async (key, setter) => {
    const stored = localStorage.getItem(SCAN_LS_KEYS[key]);
    if (stored) {
      try { setter(JSON.parse(stored)); return; } catch (e) { /* fall through */ }
    }
    // No localStorage — load from MongoDB defaults
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/scan-options/defaults`);
      if (res.ok) { const data = await res.json(); setter(data); }
    } catch (e) { console.error('Failed to load scan defaults:', e); }
  };

  // Save scan options to localStorage when changed
  const updateScanOpts = (key, setter, newVal) => {
    setter(newVal);
    localStorage.setItem(SCAN_LS_KEYS[key], JSON.stringify(newVal));
  };

  // Reset to defaults: clear localStorage, reload from MongoDB
  const resetScanOptsForDialog = async (key, setter) => {
    localStorage.removeItem(SCAN_LS_KEYS[key]);
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/scan-options/defaults`);
      if (res.ok) { const data = await res.json(); setter(data); toast.success('Reset to system defaults'); }
    } catch (e) { toast.error('Failed to load defaults'); }
  };

  // Load defaults for the settings card (always from MongoDB, ignores localStorage)
  const loadScanOptsDefaultsCard = async () => {
    setScanOptsDefaultsLoading(true);
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/scan-options/defaults`);
      if (res.ok) setScanOptsDefaults(await res.json());
    } catch (e) { console.error(e); }
    setScanOptsDefaultsLoading(false);
  };

  // Save defaults card to MongoDB
  const saveScanOptsDefaults = async () => {
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/scan-options/defaults`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scanOptsDefaults),
      });
      if (res.ok) toast.success('Scan options defaults saved');
      else toast.error('Failed to save');
    } catch (e) { toast.error('Failed to save'); }
  };

  // Language Auto-Detection Settings — Load from MongoDB
  const loadLangDetectSettings = async () => {
    setLangDetectSettingsLoading(true);
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/language-detect/settings`);
      if (res.ok) setLangDetectSettings(await res.json());
    } catch (e) { console.error('Failed to load language detect settings:', e); }
    setLangDetectSettingsLoading(false);
    // Also ensure OpenRouter models are loaded for the model picker
    if (openrouterModels.length === 0) fetchOpenRouterModels();
  };

  // Language Auto-Detection Settings — Save to MongoDB
  const saveLangDetectSettings = async () => {
    setSavingLangDetect(true);
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/language-detect/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(langDetectSettings),
      });
      if (res.ok) toast.success('Language detection settings saved');
      else { const err = await res.json(); toast.error(err.detail || 'Failed to save'); }
    } catch (e) { toast.error('Failed to save'); }
    setSavingLangDetect(false);
  };

  // Supported file types and size limits
  // SUPPORTED_EXTENSIONS moved to ./cdm/constants.js
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
  const WARN_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  // Fetch system stats
  const fetchSystemStats = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/stats/overview`);
      if (response.ok) {
        const data = await response.json();
        setSystemStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch system stats:', error);
    }
  }, []);

  // Fetch collections list
  const fetchCollections = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/collections`);
      if (response.ok) {
        const data = await response.json();
        setCollections(data.collections || ['General']);
      }
    } catch (error) {
      console.error('Failed to fetch collections:', error);
      setCollections(['General']);
    }
  }, []);

  // Fetch AI detection settings
  const fetchDetectionSettings = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/detection-settings`);
      if (response.ok) {
        const data = await response.json();
        setDetectionSettings({
          enabled: data.enabled || false,
          models: data.models || [null, null, null, null],
          prompt: data.prompt || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch detection settings:', error);
    }
  }, []);

  // Fetch OpenRouter models
  const fetchOpenRouterModels = useCallback(async () => {
    setLoadingModels(true);
    try {
      const response = await authedFetch(`${API_URL}/api/openrouter/models`);
      if (response.ok) {
        const data = await response.json();
        const models = (data.models || [])
          .filter(m => m.id && m.name)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setOpenrouterModels(models);
      }
    } catch (error) {
      console.error('Failed to fetch OpenRouter models:', error);
    }
    setLoadingModels(false);
  }, []);

  // Save AI detection settings
  const handleSaveDetectionSettings = async () => {
    setSavingDetection(true);
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/detection-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(detectionSettings),
      });
      if (response.ok) toast.success('AI Detection settings saved');
      else toast.error('Failed to save detection settings');
    } catch (e) {
      toast.error('Failed to save detection settings');
    }
    setSavingDetection(false);
  };

  // Reset detection prompt to default
  const handleResetDetectionPrompt = async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/detection-settings/reset-prompt`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        setDetectionSettings(prev => ({ ...prev, prompt: data.prompt }));
        toast.success('Prompt reset to default');
      }
    } catch (e) {
      toast.error('Failed to reset prompt');
    }
  };

  // Fetch AI detection result for a document
  const fetchAiDetection = async (docId) => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}/ai-detection`);
      if (response.ok) {
        const data = await response.json();
        setAiDetectionResult(data);
      }
    } catch (error) {
      console.error('Failed to fetch AI detection:', error);
      setAiDetectionResult(null);
    }
  };

  // Fetch Tensornex index status for a document
  const fetchTensornexStatus = async (docId) => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}/tensornex-status`);
      if (response.ok) {
        const data = await response.json();
        setTensornexStatus(data.tensornex_status || null);
      }
    } catch (error) {
      console.error('Failed to fetch Tensornex status:', error);
      setTensornexStatus(null);
    }
  };

  // Fetch response cache stats
  const fetchCacheStats = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/cache-stats`);
      if (response.ok) {
        const data = await response.json();
        setCacheStats(data);
        if (data.ttl_days) setCacheTtlDays(data.ttl_days);
      }
    } catch (error) {
      console.error('Failed to fetch cache stats:', error);
    }
  }, []);

  // Clear response cache
  const handleClearCache = async () => {
    if (!window.confirm('Clear all cached responses? Users will get fresh LLM responses on next query.')) return;
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/cache-clear`, { method: 'POST' });
      if (response.ok) {
        toast.success('Response cache cleared');
        fetchCacheStats();
      } else {
        toast.error('Failed to clear cache');
      }
    } catch (e) { toast.error('Failed to clear cache'); }
  };

  // Save cache TTL
  const handleSaveCacheTtl = async () => {
    setSavingCacheTtl(true);
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/cache-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ttl_days: parseInt(cacheTtlDays) || 7 }),
      });
      if (response.ok) toast.success(`Cache TTL set to ${cacheTtlDays} days`);
      else toast.error('Failed to save cache TTL');
    } catch (e) { toast.error('Failed to save cache TTL'); }
    setSavingCacheTtl(false);
  };

  // Fetch cached response list
  const fetchCacheList = async () => {
    setLoadingCacheList(true);
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/cache-list`);
      if (response.ok) {
        const data = await response.json();
        setCacheEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Failed to fetch cache list:', error);
    }
    setLoadingCacheList(false);
  };

  // Delete a specific cached entry
  const handleDeleteCacheEntry = async (cacheKey) => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/cache/${cacheKey}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Cached response deleted');
        setCacheEntries(prev => prev.filter(e => e.cache_key !== cacheKey));
        fetchCacheStats();
      } else {
        toast.error('Failed to delete');
      }
    } catch (e) { toast.error('Failed to delete'); }
  };

  // Fetch bulk graph status for all documents
  const fetchGraphStatusBulk = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/graph-status-bulk`);
      if (response.ok) {
        const data = await response.json();
        setGraphStatusBulk(data.statuses || {});
      }
    } catch (error) {
      console.error('Failed to fetch graph status:', error);
    }
  }, []);

  // Fetch enrichment progress (RAG V5)
  const fetchEnrichmentProgress = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/enrichment-progress`);
      if (response.ok) {
        const data = await response.json();
        setEnrichmentProgress(data);
      }
    } catch (error) {
      console.error('Failed to fetch enrichment progress:', error);
    }
  }, []);

  // Fetch system health status (Level 7)
  const fetchSystemHealth = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/system/health-status`);
      if (response.ok) setSystemHealth(await response.json());
    } catch (error) {
      console.error('Failed to fetch system health:', error);
    }
  }, []);

  // Fetch system capacity (Level 6)
  const fetchSystemCapacity = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/system/capacity`);
      if (response.ok) setSystemCapacity(await response.json());
    } catch (error) {
      console.error('Failed to fetch system capacity:', error);
    }
  }, []);

  // Recheck: reset the (stale-open) circuit breaker for this group, THEN refresh status.
  // The breaker can stay "open" after a transient outage even once the service recovers;
  // resetting flips it closed so a healthy service clears the banner immediately.
  const handleRecheckBreaker = useCallback(async (group) => {
    try {
      await authedFetch(`${API_URL}/api/admin/rag-infrastructure/concurrency/circuit-breakers/${group}/reset`, { method: 'POST' });
    } catch (e) {
      console.error('Failed to reset circuit breaker:', e);
    }
    await fetchSystemHealth();
    await fetchSystemCapacity();
  }, [fetchSystemHealth, fetchSystemCapacity]);

  // Fetch per-document graph status
  const fetchDocGraphStatus = async (docId) => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}/graph-status`);
      if (response.ok) {
        const data = await response.json();
        setDocGraphStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch doc graph status:', error);
      setDocGraphStatus(null);
    }
  };

  // Fetch summary settings
  const fetchSummarySettings = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/summary-settings`);
      if (response.ok) {
        const data = await response.json();
        setSummarySettings(data);
      }
    } catch (error) { console.error('Failed to fetch summary settings:', error); }
  }, []);

  // Fetch summary stats
  const fetchSummaryStats = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/summary-stats`);
      if (response.ok) setSummaryStats(await response.json());
    } catch (error) { console.error('Failed to fetch summary stats:', error); }
  }, []);

  // Save summary settings
  const handleSaveSummarySettings = async () => {
    setSavingSummary(true);
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/summary-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(summarySettings),
      });
      if (res.ok) { toast.success('Summary settings saved'); fetchSummaryStats(); }
      else toast.error('Failed to save');
    } catch (e) { toast.error('Failed to save'); }
    setSavingSummary(false);
  };

  // Generate summaries for all documents
  const handleGenerateAllSummaries = async () => {
    setGeneratingAllSummaries(true);
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/maintenance/generate-summaries`, { method: 'POST' });
      if (res.ok) {
        toast.success('Summary generation started in background');
        setSummaryJobPolling(true);
        setTimeout(() => fetchSummaryBulkStatus(), 2000);
      }
      else toast.error('Failed to start');
    } catch (e) { toast.error('Failed to start'); }
    setGeneratingAllSummaries(false);
  };

  // Fetch per-document summary status
  const fetchDocSummaryStatus = async (docId) => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}/summary-status`);
      if (response.ok) setDocSummaryStatus(await response.json());
    } catch (error) { setDocSummaryStatus(null); }
  };

  // Fetch bulk summary status for all documents
  const fetchSummaryBulkStatus = async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/summary-status-bulk`);
      if (response.ok) {
        const data = await response.json();
        setSummaryBulkStatus(data);
        // If job is running, keep polling
        if (data.job?.status === 'running') {
          setSummaryJobPolling(true);
        } else {
          setSummaryJobPolling(false);
        }
      }
    } catch (error) { console.error('Failed to fetch summary bulk status:', error); }
  };

  // Poll summary generation progress when job is running
  React.useEffect(() => {
    if (!summaryJobPolling) return;
    const interval = setInterval(() => { fetchSummaryBulkStatus(); fetchSummaryStats(); }, 5000);
    return () => clearInterval(interval);
  }, [summaryJobPolling]);

  // KB Article Import functions
  const fetchKbCategories = async () => {
    setLoadingKbCats(true);
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/kb-import/categories`);
      if (response.ok) {
        const data = await response.json();
        setKbCategories(data.categories || []);
      }
    } catch (e) { console.error('Failed to fetch KB categories:', e); }
    setLoadingKbCats(false);
  };

  const fetchKbImportStatus = async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/kb-import/status`);
      if (response.ok) {
        const data = await response.json();
        setKbImportStatus(data);
        if (data.status === 'running' || data.status === 'fetching') {
          setKbImportPolling(true);
        } else {
          setKbImportPolling(false);
        }
      }
    } catch (e) { console.error('Failed to fetch KB import status:', e); }
  };

  const fetchKbCategoryArticles = async (categoryId) => {
    if (kbArticlesByCat[categoryId]) return; // already loaded
    setKbLoadingArts(prev => [...prev, categoryId]);
    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/kb-import/categories/${categoryId}/articles`);
      if (response.ok) {
        const data = await response.json();
        setKbArticlesByCat(prev => ({ ...prev, [categoryId]: data.articles || [] }));
      }
    } catch (e) { console.error('Failed to fetch KB articles:', e); }
    setKbLoadingArts(prev => prev.filter(c => c !== categoryId));
  };

  const toggleKbExpand = (categoryId) => {
    setKbExpanded(prev => {
      if (prev.includes(categoryId)) return prev.filter(c => c !== categoryId);
      fetchKbCategoryArticles(categoryId);
      return [...prev, categoryId];
    });
  };

  const toggleKbCategory = (categoryId) => {
    setKbSelectedCats(prev => prev.includes(categoryId)
      ? prev.filter(c => c !== categoryId)
      : [...prev, categoryId]);
  };

  const toggleKbArticle = (articleId) => {
    setKbSelectedArticles(prev => prev.includes(articleId)
      ? prev.filter(a => a !== articleId)
      : [...prev, articleId]);
  };

  const handleStartKbImport = async () => {
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/kb-import/sync`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_ids: kbSelectedCats.length > 0 ? kbSelectedCats : null,
          article_ids: kbSelectedArticles.length > 0 ? kbSelectedArticles : null,
          scan_options: scanOptsKb,
        }),
      });
      if (res.ok) {
        toast.success('KB article import started');
        setKbImportPolling(true);
        setTimeout(fetchKbImportStatus, 2000);
      } else toast.error('Failed to start import');
    } catch (e) { toast.error('Failed to start import'); }
  };

  const handleStopKbImport = async () => {
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/kb-import/stop`, { method: 'POST' });
      if (res.ok) toast.success('Stop requested — import will stop after current article');
      else toast.error('Failed to stop');
    } catch (e) { toast.error('Failed to stop'); }
  };

  // URL Import handlers
  const handleScanUrl = async () => {
    if (!urlInput.trim()) return;
    setScanningUrl(true);
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/url-import/scan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: urlInput.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setScannedPages(data.pages || []);
        setSelectedUrls([]);
      } else toast.error('Failed to scan URL');
    } catch (e) { toast.error('Failed to scan URL'); }
    setScanningUrl(false);
  };

  const handleImportUrls = async (urls) => {
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/url-import/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls, collection_name: urlCollectionName || 'URLs', scan_options: scanOptsUrl }),
      });
      const data = await res.json();
      // Phase 11.5: Handle 409 duplicate detection
      if (data.error === 'url_already_imported' && data.existing_document) {
        const existing = data.existing_document;
        const shouldReplace = confirm(
          `URL already imported\n\nThis URL was imported on ${new Date(existing.ingested_at).toLocaleDateString()} as "${existing.title}" (version: ${existing.version_id || 'v1'}).\n\nDo you want to update to a new version?`
        );
        if (shouldReplace) {
          const replaceRes = await authedFetch(`${API_URL}/api/rag/cdm/documents/${existing.id}/replace`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: urls[0] }),
          });
          const replaceData = await replaceRes.json();
          if (replaceData.success) {
            toast.success(`Updated to ${replaceData.new_version}. ${replaceData.preserved_chunks} edited chunks preserved.`);
            fetchDocuments();
          } else toast.error(replaceData.detail || 'Failed to replace');
        }
        return;
      }
      if (res.ok || data.success) {
        const batchId = data.batch_id;
        const taskCount = data.task_count || urls.length;
        if (batchId) {
          toast.success(`Import started — ${taskCount} URL${taskCount !== 1 ? 's' : ''} queued. Track progress below.`, { duration: 5000 });
          startPolling();
        } else {
          toast.success('URL import started');
        }
        setTimeout(fetchUrlImportStatus, 2000);
      }
      else toast.error('Failed to start URL import');
    } catch (e) { toast.error('Failed'); }
  };

  const fetchUrlImportStatus = async () => {
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/url-import/status`);
      if (res.ok) setUrlImportStatus(await res.json());
    } catch (e) {}
  };

  const handleBulkUpdate = async () => {
    if (selectedDocIds.length === 0) return;
    const body = { doc_ids: selectedDocIds };
    if (bulkType) body.doc_type = bulkType;
    if (bulkCollection) body.collection_id = bulkCollection;
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/bulk-update`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Updated ${data.modified} documents`);
        setSelectedDocIds([]);
        setBulkType('');
        setBulkCollection('');
        fetchDocuments();
      } else toast.error('Bulk update failed');
    } catch (e) { toast.error('Bulk update failed'); }
  };

  // Re-embed a single document with the active embedding model (safe, embed-then-replace)
  const handleReembedDoc = async (docId) => {
    setReembeddingDocIds(prev => [...prev, docId]);
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}/reembed`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Re-embedded ${data.chunks || 0} chunks → ${data.model || 'active model'}`, { duration: 6000 });
        fetchDocuments();
      } else {
        let msg = 'Re-embed failed';
        try { msg = (await res.json()).detail || msg; } catch (e) {}
        toast.error(msg);
      }
    } catch (e) {
      toast.error('Re-embed failed');
    } finally {
      setReembeddingDocIds(prev => prev.filter(id => id !== docId));
    }
  };

  // Re-embed all selected documents sequentially, with per-row progress feedback
  const handleBulkReembed = async () => {
    const ids = [...selectedDocIds];
    if (ids.length === 0) return;
    let done = 0, failed = 0;
    setBulkReembedProgress({ current: 0, total: ids.length });
    for (const docId of ids) {
      setReembeddingDocIds(prev => [...prev, docId]);
      try {
        const res = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}/reembed`, { method: 'POST' });
        if (res.ok) done++; else failed++;
      } catch (e) {
        failed++;
      } finally {
        setReembeddingDocIds(prev => prev.filter(id => id !== docId));
      }
      setBulkReembedProgress({ current: done + failed, total: ids.length });
      fetchDocuments();
    }
    setBulkReembedProgress(null);
    setSelectedDocIds([]);
    toast.success(`Re-embed complete: ${done} succeeded${failed > 0 ? `, ${failed} failed` : ''}`);
    fetchDocuments();
  };

  const handleCheckOrphans = async () => {
    setCheckingOrphans(true);
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/tensornex-orphans`);
      if (res.ok) setOrphanData(await res.json());
      else toast.error('Failed to check orphans');
    } catch (e) { toast.error('Failed'); }
    setCheckingOrphans(false);
  };

  const handleDeleteOrphans = async () => {
    if (!window.confirm('Delete all orphan vectors from Tensornex?')) return;
    setDeletingOrphans(true);
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/tensornex-orphans/delete`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Deleted ${data.orphans_deleted} orphans`);
        handleCheckOrphans();
      } else toast.error('Failed');
    } catch (e) { toast.error('Failed'); }
    setDeletingOrphans(false);
  };

  const handleDeleteAll = async () => {
    const confirm1 = window.prompt('This will permanently delete ALL documents, KB articles, chunks, embeddings, and cache. Type DELETE to confirm:');
    if (confirm1 !== 'DELETE') return;
    try {
      const res = await authedFetch(`${API_URL}/api/rag/cdm/delete-all`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Deleted: ${data.deleted?.documents || 0} documents, ${data.deleted?.chunks || 0} chunks`);
        fetchDocuments();
        fetchSystemStats();
        fetchCacheStats();
        fetchKbImportStatus();
      } else toast.error('Delete failed');
    } catch (e) { toast.error('Delete failed'); }
  };

  React.useEffect(() => {
    if (!kbImportPolling) return;
    const interval = setInterval(fetchKbImportStatus, 5000);
    return () => clearInterval(interval);
  }, [kbImportPolling]);

  // Fetch documents list
  const fetchDocuments = useCallback(async () => {
    try {
      let url = `${API_URL}/api/rag/cdm/documents?page=${page}&page_size=${pageSize}`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (collectionFilter && collectionFilter !== 'all') {
        url += `&collection_id=${encodeURIComponent(collectionFilter)}`;
      }
      if (parentFilter && parentFilter !== 'all') {
        url += `&parent_filter=${parentFilter}`;
      }
      if (reviewFilter && reviewFilter !== 'all') {
        url += `&review_filter=${reviewFilter}`;
      }
      if (embeddingFilter && embeddingFilter !== 'all') {
        url += `&embedding_filter=${embeddingFilter}`;
      }
      if (searchQuery && searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      
      const response = await authedFetch(url);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents);
        setTotalDocs(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to fetch documents');
    }
  }, [page, pageSize, statusFilter, collectionFilter, parentFilter, reviewFilter, embeddingFilter, searchQuery]);

  // Sync filter state to URL params (Path B: retrofit for all filters + search)
  useEffect(() => {
    const params = {};
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (collectionFilter && collectionFilter !== 'all') params.collection = collectionFilter;
    if (parentFilter && parentFilter !== 'all') params.parent_filter = parentFilter;
    if (reviewFilter && reviewFilter !== 'all') params.review_filter = reviewFilter;
    if (embeddingFilter && embeddingFilter !== 'all') params.embedding_filter = embeddingFilter;
    if (searchQuery && searchQuery.trim()) params.search = searchQuery.trim();
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [statusFilter, collectionFilter, parentFilter, reviewFilter, embeddingFilter, searchQuery, page, setSearchParams]);

  // Search-on-submit: execute search only on Enter or button click, not on every keystroke
  const executeSearch = () => {
    setSearchQuery(searchInput.trim());
    setPage(1);
  };
  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(1);
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSystemStats(), fetchDocuments(), fetchCollections(), fetchRagRules(), fetchSystemBehaviorRules(), fetchDetectionSettings(), fetchOpenRouterModels(), fetchCacheStats(), fetchGraphStatusBulk(), fetchSummarySettings(), fetchSummaryStats(), fetchEnrichmentProgress(), fetchSystemHealth(), fetchSystemCapacity()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSystemStats, fetchDocuments, fetchCollections, fetchDetectionSettings, fetchOpenRouterModels, fetchCacheStats, fetchGraphStatusBulk, fetchSummarySettings, fetchSummaryStats, fetchEnrichmentProgress, fetchSystemHealth, fetchSystemCapacity]);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchSystemStats(), fetchDocuments(), fetchCollections(), fetchRagRules(), fetchEnrichmentProgress()]);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // Poll enrichment progress when there are active enrichments (RAG V5)
  React.useEffect(() => {
    if (!enrichmentProgress) return;
    const activeCount = (enrichmentProgress.counts?.pending || 0) + (enrichmentProgress.counts?.in_progress || 0);
    if (activeCount === 0) return;
    const interval = setInterval(() => { fetchEnrichmentProgress(); fetchDocuments(); }, 5000);
    return () => clearInterval(interval);
  }, [enrichmentProgress, fetchEnrichmentProgress, fetchDocuments]);

  // RAG Rules
  const fetchRagRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await authedFetch(`${API_URL}/api/rag/cdm/rag-rules`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setRagRules(data.rules || "");
      }
    } catch (e) { console.error("Failed to fetch RAG rules:", e); }
  };

  const handleSaveRagRules = async () => {
    setSavingRules(true);
    try {
      const token = localStorage.getItem('token');
      const res = await authedFetch(`${API_URL}/api/rag/cdm/rag-rules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rules: ragRules }),
      });
      if (res.ok) {
        toast.success('RAG rules saved');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save RAG rules');
      }
    } catch (e) {
      console.error('Save RAG rules error:', e);
      toast.error('Failed to save RAG rules');
    }
    setSavingRules(false);
  };

  const handleResetRagRules = async () => {
    if (!window.confirm('Reset RAG rules to defaults?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await authedFetch(`${API_URL}/api/rag/cdm/rag-rules/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('RAG rules reset to defaults');
        fetchRagRules();
      } else {
        toast.error('Failed to reset RAG rules');
      }
    } catch (e) {
      console.error('Reset RAG rules error:', e);
      toast.error('Failed to reset RAG rules');
    }
  };

  // System Behavior Rules
  const fetchSystemBehaviorRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await authedFetch(`${API_URL}/api/rag/cdm/system-behavior-rules`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSystemRules(data.rules || "");
      }
    } catch (e) { console.error("Failed to fetch system behavior rules:", e); }
  };

  const handleSaveSystemRules = async () => {
    setSavingSystemRules(true);
    try {
      const token = localStorage.getItem('token');
      const res = await authedFetch(`${API_URL}/api/rag/cdm/system-behavior-rules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rules: systemRules }),
      });
      if (res.ok) toast.success('System behavior rules saved');
      else toast.error('Failed to save');
    } catch (e) { toast.error('Failed to save system behavior rules'); }
    setSavingSystemRules(false);
  };

  const handleResetSystemRules = async () => {
    if (!window.confirm('Reset system behavior rules to defaults?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await authedFetch(`${API_URL}/api/rag/cdm/system-behavior-rules/reset`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success('System behavior rules reset'); fetchSystemBehaviorRules(); }
      else toast.error('Failed to reset');
    } catch (e) { toast.error('Failed to reset'); }
  };

  // Fetch document detail
  const fetchDocumentDetail = async (docId) => {
    setDetailLoading(true);
    try {
      const [detailRes, statsRes, nodesRes, chunksRes] = await Promise.all([
        authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}`),
        authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}/stats`),
        authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}/nodes?page=1&page_size=50`),
        authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}/chunks?page=1&page_size=50`)
      ]);

      if (detailRes.ok) {
        setDocDetail(await detailRes.json());
      }
      if (statsRes.ok) {
        setDocStats(await statsRes.json());
      }
      if (nodesRes.ok) {
        const nodesData = await nodesRes.json();
        setDocNodes(nodesData.nodes);
        setTotalNodes(nodesData.total);
      }
      if (chunksRes.ok) {
        const chunksData = await chunksRes.json();
        setDocChunks(chunksData.chunks);
        setTotalChunks(chunksData.total);
      }
    } catch (error) {
      console.error('Failed to fetch document detail:', error);
      toast.error('Failed to load document details');
    }
    setDetailLoading(false);
  };

  // Open document detail
  const handleViewDocument = (doc) => {
    setSelectedDoc(doc);
    setNodesPage(1);
    setChunksPage(1);
    setAiDetectionResult(null);
    setTensornexStatus(null);
    setDocGraphStatus(null);
    setDocSummaryStatus(null);
    fetchDocumentDetail(doc.doc_id);
    fetchAiDetection(doc.doc_id);
    fetchTensornexStatus(doc.doc_id);
    fetchDocGraphStatus(doc.doc_id);
    fetchDocSummaryStatus(doc.doc_id);
  };

  // Delete document
  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document? This will soft-delete it and all related data.')) {
      return;
    }

    try {
      const response = await authedFetch(`${API_URL}/api/rag/cdm/documents/${docId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const result = await response.json();
        const ext = result.external_services || {};
        const services = [];
        if (ext.qdrant?.deleted) services.push('Qdrant');
        if (ext.elasticsearch?.deleted) services.push('Elasticsearch');
        if (ext.neo4j?.deleted) services.push('Neo4j');
        
        const warnings = [];
        if (ext.qdrant && !ext.qdrant.deleted) warnings.push('Qdrant');
        if (ext.elasticsearch && !ext.elasticsearch.deleted) warnings.push('Elasticsearch');
        if (ext.neo4j && !ext.neo4j.deleted) warnings.push('Neo4j');
        
        let msg = `Document deleted: ${result.deleted_counts.documents} doc, ${result.deleted_counts.nodes} nodes, ${result.deleted_counts.chunks} chunks`;
        if (services.length > 0) msg += `. Removed from ${services.join(', ')}`;
        if (warnings.length > 0) msg += `. Warning: failed to remove from ${warnings.join(', ')}`;
        
        toast.success(msg);
        setSelectedDoc(null);
        fetchDocuments();
        fetchSystemStats();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    }
  };

  // Format date
  // formatDate / truncateText moved to ./cdm/constants.js

  // Validate file for upload
  const validateFile = (file) => {
    if (!file) return 'No file selected';
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!SUPPORTED_EXTENSIONS.includes(ext)) {
      return `Unsupported file type "${ext}". Supported: PDF, DOCX, XLSX, PPTX, CSV, TXT, Images, Audio, Video`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum 100MB.`;
    }
    return null;
  };

  // Handle file selection (from picker or drag-drop) — supports multiple
  const handleFileSelect = (fileList) => {
    setUploadError('');
    const newFiles = Array.from(fileList);

    // Max 100 files
    if (uploadFiles.length + newFiles.length > 100) {
      setUploadError('Maximum 100 files per upload. Please reduce your selection.');
      return;
    }

    const validated = [];
    for (const file of newFiles) {
      const error = validateFile(file);
      validated.push({ file, status: error ? 'invalid' : 'pending', error: error || null });
    }

    const combined = [...uploadFiles, ...validated];

    // Total size check
    const totalSize = combined.reduce((sum, f) => sum + f.file.size, 0);
    if (totalSize > 2 * 1024 * 1024 * 1024) {
      setUploadError('Total batch size exceeds 2GB. Please reduce your selection.');
      return;
    }
    if (totalSize > 500 * 1024 * 1024) {
      toast.warning(`Large batch (${(totalSize / 1024 / 1024).toFixed(0)}MB). Upload may take a while.`);
    }

    setUploadFiles(combined);
  };

  // Remove a file from the list
  const removeUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle upload — sequential, one file at a time
  const handleUpload = async () => {
    const validFiles = uploadFiles.filter(f => f.status === 'pending');
    if (validFiles.length === 0) return;

    setUploading(true);
    setUploadCancelled(false);
    uploadCancelledRef.current = false;
    setUploadError('');

    const total = validFiles.length;
    let succeeded = 0;
    let failed = 0;

    setUploadProgress({ current: 0, total, succeeded: 0, failed: 0 });

    // Track results for each file
    const updatedFiles = [...uploadFiles];

    for (let i = 0; i < uploadFiles.length; i++) {
      if (uploadFiles[i].status !== 'pending') continue;
      if (uploadCancelledRef.current) break;

      const fileEntry = uploadFiles[i];
      setUploadProgress({ current: succeeded + failed + 1, total, succeeded, failed });

      try {
        const formData = new FormData();
        formData.append('file', fileEntry.file);
        formData.append('title', fileEntry.file.name.replace(/\.[^/.]+$/, ''));
        formData.append('collection_id', uploadCollectionId.trim() || 'General');
        if (scanOptsUpload) {
          formData.append('scan_options', JSON.stringify(scanOptsUpload));
        }

        const response = await authedFetch(`${API_URL}/api/rag/ingest/file`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          let errMsg = `Upload failed (${response.status})`;
          try {
            const errText = await response.text();
            const parsed = JSON.parse(errText);
            errMsg = parsed.detail || errMsg;
          } catch (e) {
            errMsg = `Upload failed: ${response.statusText || response.status}`;
          }
          throw new Error(errMsg);
        }

        // Read response once — don't clone
        try {
          await response.json();
        } catch (e) {
          // Response body consumed — that's OK, upload succeeded
        }

        updatedFiles[i] = { ...updatedFiles[i], status: 'success', error: null };
        succeeded++;
      } catch (err) {
        updatedFiles[i] = { ...updatedFiles[i], status: 'failed', error: err.message };
        failed++;
      }

      setUploadFiles([...updatedFiles]);
      setUploadProgress({ current: succeeded + failed, total, succeeded, failed });
      
      // Small delay between uploads to prevent server overload
      if (i < uploadFiles.length - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }

    setUploading(false);

    if (uploadCancelledRef.current) {
      toast.info(`Upload cancelled. ${succeeded} uploaded, ${failed} failed, ${total - succeeded - failed} skipped.`);
    } else {
      toast.success(`Upload complete: ${succeeded} successful${failed > 0 ? `, ${failed} failed` : ''}`);
    }

    // Refresh after uploads + auto-close + start polling
    setTimeout(() => {
      fetchSystemStats();
      fetchDocuments();
      fetchCollections();
    }, 2000);
    
    // Auto-close popup after 1.5 seconds
    setTimeout(() => {
      setShowUploadDialog(false);
    }, 1500);
    
    // Start polling for real-time status updates
    startStatusPolling();
  };

  // Polling for real-time document status
  const statusPollRef = React.useRef(null);
  const startStatusPolling = () => {
    if (statusPollRef.current) clearInterval(statusPollRef.current);
    statusPollRef.current = setInterval(async () => {
      await fetchDocuments();
      await fetchSystemStats();
      // Check if all documents are in final state
      if (documents.every(d => ['indexed', 'failed'].includes(d.processing_status))) {
        clearInterval(statusPollRef.current);
        statusPollRef.current = null;
      }
    }, 5000);
    // Auto-stop after 5 minutes
    setTimeout(() => {
      if (statusPollRef.current) {
        clearInterval(statusPollRef.current);
        statusPollRef.current = null;
      }
    }, 300000);
  };

  React.useEffect(() => {
    return () => { if (statusPollRef.current) clearInterval(statusPollRef.current); };
  }, []);

  // Reset upload dialog
  const resetUploadDialog = () => {
    setUploadFiles([]);
    setUploadCollectionId('General');
    setUploadError('');
    setDragOver(false);
    setShowNewCollection(false);
    setNewCollectionName('');
    setUploadProgress({ current: 0, total: 0, succeeded: 0, failed: 0 });
    setUploadCancelled(false);
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalDocs / pageSize);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="cdm-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading CDM data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="cdm-documents-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CDM Documents</h1>
          <p className="text-gray-500 text-sm mt-1">
            Canonical Document Model - View and manage ingested documents
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPipelineConfig(true)}
            data-testid="pipeline-config-btn"
          >
            <Settings className="h-4 w-4 mr-1" />
            Pipeline Settings
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowHelpGuide(true)}
            data-testid="help-guide-btn"
          >
            <BookOpen className="h-4 w-4 mr-2" />
            How It Works
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const res = await authedFetch(`${API_URL}/api/rag/cdm/cleanup-orphans`, {
                  method: 'POST', headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                  const data = await res.json();
                  toast.success(`Cleaned up: ${data.deleted.nodes} nodes, ${data.deleted.chunks} chunks, ${data.deleted.pages} pages`);
                  fetchSystemStats();
                  fetchDocuments();
                } else {
                  toast.error('Cleanup failed');
                }
              } catch (e) { toast.error('Cleanup failed'); }
            }}
            data-testid="cleanup-orphans-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clean Up
          </Button>
          <Button
            onClick={() => { resetUploadDialog(); setShowUploadDialog(true); loadScanOptsForDialog('upload', setScanOptsUpload); fetchSystemCapacity(); }}
            data-testid="upload-doc-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Active Imports — pinned at top & sticky so it's visible on refresh while imports run */}
      <div className="sticky top-2 z-30" data-testid="active-imports-sticky-wrap">
        <BatchImportStatus
          batchDetails={batchDetails}
          activeBatches={activeBatches}
          isPolling={isPolling}
          onCancelAll={handleCancelAllImports}
        />
      </div>

      {/* Level 7: System Health Alerts Banner */}
      {systemHealth && systemHealth.alerts && systemHealth.alerts.length > 0 && (
        <div className="space-y-2" data-testid="health-alerts-banner">
          {systemHealth.alerts.map((alert, i) => (
            <div key={i} className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
              alert.severity === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
            }`} data-testid={`health-alert-${alert.group}`}>
              {alert.severity === 'error'
                ? <XCircle className="h-5 w-5 shrink-0 mt-0.5 text-red-500" />
                : <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-amber-500" />
              }
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{alert.label}</p>
                <p className="text-xs mt-0.5 opacity-80">{alert.message}</p>
              </div>
              <button
                className={`shrink-0 flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                  alert.severity === 'error'
                    ? 'border-red-300 text-red-700 hover:bg-red-100'
                    : 'border-amber-300 text-amber-700 hover:bg-amber-100'
                }`}
                onClick={async (e) => {
                  const btn = e.currentTarget;
                  btn.disabled = true;
                  await handleRecheckBreaker(alert.group);
                  btn.disabled = false;
                  toast.info('Connection rechecked');
                }}
                data-testid={`recheck-btn-${alert.group}`}
              >
                <RefreshCw className="h-3 w-3" />
                Recheck
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Level 6: Queue Capacity Warning */}
      {systemCapacity && systemCapacity.level !== 'ok' && (
        <div className={`flex items-center gap-3 px-4 py-2 rounded-lg border ${
          systemCapacity.level === 'critical'
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-amber-50 border-amber-200 text-amber-800'
        }`} data-testid="capacity-warning-banner">
          {systemCapacity.level === 'critical'
            ? <XCircle className="h-4 w-4 shrink-0 text-red-500" />
            : <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          }
          <span className="text-xs">{systemCapacity.reason}</span>
          <span className="ml-auto text-[10px] opacity-60">{systemCapacity.total_pending}/{systemCapacity.max_queued} tasks</span>
        </div>
      )}

      {/* Overview Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="total-documents-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Documents</p>
                <p className="text-3xl font-bold">{systemStats?.total_documents || 0}</p>
                <div className="flex gap-3 mt-1 text-[10px] text-gray-400">
                  <span>{systemStats?.parents_count || 0} parents</span>
                  <span>{systemStats?.children_count || 0} children</span>
                </div>
              </div>
              <FileText className="h-10 w-10 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="total-nodes-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Nodes</p>
                <p className="text-3xl font-bold">{systemStats?.total_nodes || 0}</p>
              </div>
              <Layers className="h-10 w-10 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="total-chunks-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Chunks</p>
                <p className="text-3xl font-bold">{systemStats?.total_chunks || 0}</p>
              </div>
              <Box className="h-10 w-10 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="total-pages-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Pages</p>
                <p className="text-3xl font-bold">{systemStats?.total_pages || 0}</p>
              </div>
              <Database className="h-10 w-10 text-cyan-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Type Breakdown */}
      {systemStats?.type_breakdown && (
        <div className="grid grid-cols-3 md:grid-cols-7 gap-2" data-testid="type-breakdown">
          {[
            { key: 'articles', label: 'Articles', icon: '📚' },
            { key: 'pdfs', label: 'PDFs', icon: '📄' },
            { key: 'urls', label: 'URLs', icon: '🌐' },
            { key: 'videos', label: 'Videos', icon: '🎥' },
            { key: 'audio', label: 'Audio', icon: '🎵' },
            { key: 'images', label: 'Images', icon: '🖼️' },
            { key: 'other', label: 'Other', icon: '📁' },
          ].filter(t => systemStats.type_breakdown[t.key] > 0).map(t => (
            <div key={t.key} className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg text-xs">
              <span>{t.icon}</span>
              <span className="font-bold">{systemStats.type_breakdown[t.key]}</span>
              <span className="text-gray-500">{t.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Children by Media Source (Phase 9) */}
      {systemStats?.children_by_media_source && Object.keys(systemStats.children_by_media_source).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="children-by-source">
          <span className="text-xs text-gray-400 self-center">Children:</span>
          {Object.entries(systemStats.children_by_media_source).sort((a, b) => b[1] - a[1]).map(([source, count]) => (
            <div key={source} className="flex items-center gap-1 px-2 py-0.5 bg-violet-50 rounded text-[10px] text-violet-700">
              <span className="font-bold">{count}</span>
              <span>{source}</span>
            </div>
          ))}
        </div>
      )}

      {/* Enrichment Progress (RAG V5) */}
      {enrichmentProgress && (enrichmentProgress.counts?.in_progress > 0 || enrichmentProgress.counts?.pending > 0 || enrichmentProgress.embedding_count > 0) && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-blue-50/60 rounded-lg border border-blue-100" data-testid="enrichment-progress-bar">
          <Activity className="h-4 w-4 text-blue-500" />
          <span className="text-xs font-medium text-blue-700">Pipeline Activity:</span>
          {enrichmentProgress.embedding_count > 0 && (
            <div className="flex items-center gap-1 text-xs text-cyan-700">
              <Database className="w-3 h-3" />
              <span className="font-bold">{enrichmentProgress.embedding_count}</span>
              <span>Embedding</span>
            </div>
          )}
          {enrichmentProgress.counts?.in_progress > 0 && (
            <div className="flex items-center gap-1 text-xs text-blue-700">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span className="font-bold">{enrichmentProgress.counts.in_progress}</span>
              <span>Enriching</span>
            </div>
          )}
          {enrichmentProgress.counts?.pending > 0 && (
            <div className="flex items-center gap-1 text-xs text-amber-700">
              <Clock className="w-3 h-3" />
              <span className="font-bold">{enrichmentProgress.counts.pending}</span>
              <span>Pending</span>
            </div>
          )}
          {enrichmentProgress.active && enrichmentProgress.active.length > 0 && (
            <div className="ml-auto flex items-center gap-1 text-[10px] text-blue-500">
              {enrichmentProgress.active.slice(0, 2).map(a => (
                <span key={a.doc_id} className="bg-blue-100 px-1.5 py-0.5 rounded truncate max-w-[140px]" title={a.title}>
                  {a.title}: {a.media_processed || 0}/{a.media_found || 0}
                </span>
              ))}
              {enrichmentProgress.active.length > 2 && <span>+{enrichmentProgress.active.length - 2} more</span>}
            </div>
          )}
        </div>
      )}

      {/* Status Distribution */}
      {systemStats && Object.keys(systemStats.documents_by_status || {}).length > 0 && (
        <Card data-testid="status-distribution-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Documents by Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(systemStats.documents_by_status).map(([status, count]) => {
                const StatusIcon = STATUS_ICONS[status] || AlertCircle;
                return (
                  <div
                    key={status}
                    className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-2"
                  >
                    <StatusIcon className={`h-4 w-4 ${status === 'processing' ? 'animate-spin' : ''}`} />
                    <span className="capitalize font-medium">{status}</span>
                    <Badge className={STATUS_COLORS[status]}>{count}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Response Cache Stats */}
      {cacheStats && (
        <Card data-testid="cache-stats-card">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="h-5 w-5 text-amber-500" />
                <div>
                  <p className="font-medium text-sm">Response Cache</p>
                  <p className="text-xs text-gray-500">
                    {cacheStats.cached_queries || 0} cached responses | TTL: {cacheTtlDays} days
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => { setShowCacheList(true); fetchCacheList(); }} data-testid="view-cache-btn">
                  <Eye className="h-3 w-3 mr-1" />
                  View Cached Responses
                </Button>
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={handleClearCache} data-testid="clear-cache-btn">
                  <Trash2 className="h-3 w-3 mr-1" />
                  Clear All
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1 border-t">
              <span className="text-xs text-gray-500 shrink-0">Cache Duration:</span>
              <Input
                type="number"
                min={1}
                value={cacheTtlDays}
                onChange={(e) => setCacheTtlDays(e.target.value)}
                className="w-20 h-7 text-xs"
                data-testid="cache-ttl-input"
              />
              <span className="text-xs text-gray-500">days</span>
              <Button size="sm" className="text-xs h-7 ml-2" onClick={handleSaveCacheTtl} disabled={savingCacheTtl} data-testid="save-cache-ttl-btn">
                {savingCacheTtl ? '...' : 'Save'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cached Responses Dialog */}
      <Dialog open={showCacheList} onOpenChange={setShowCacheList}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              Cached Responses ({cacheEntries.length})
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-3 pr-1">
            {loadingCacheList ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : cacheEntries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Zap className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No cached responses</p>
              </div>
            ) : (
              cacheEntries.map((entry) => (
                <Card key={entry.cache_key} className="border">
                  <CardContent className="py-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500">Query</p>
                        <p className="text-sm font-medium">{entry.query || 'Unknown query'}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-600 shrink-0"
                        onClick={() => handleDeleteCacheEntry(entry.cache_key)}
                        data-testid={`delete-cache-${entry.cache_key}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Cached Answer</p>
                      <p className="text-xs text-gray-700 whitespace-pre-wrap max-h-24 overflow-y-auto bg-gray-50 p-2 rounded">
                        {entry.answer || 'No answer stored'}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-[10px] text-gray-400">
                      {entry.cached_at && <span>Cached: {new Date(entry.cached_at).toLocaleString()}</span>}
                      {entry.confidence && <span>Confidence: {entry.confidence}</span>}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Scan Options Defaults Card */}
      <Card data-testid="scan-options-defaults-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="h-5 w-5 text-gray-500" />
                Scan Options Defaults
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">System-wide defaults for media extraction. Applied to all imports unless overridden per-import.</p>
            </div>
            <Button size="sm" className="text-xs h-7" onClick={saveScanOptsDefaults} data-testid="save-scan-defaults-btn">
              <Save className="h-3 w-3 mr-1" /> Save Defaults
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {scanOptsDefaults ? (
            <ScanOptionsPanel
              value={scanOptsDefaults}
              onChange={setScanOptsDefaults}
              onResetToDefaults={loadScanOptsDefaultsCard}
              collapsible={false}
              defaultExpanded={true}
              loading={scanOptsDefaultsLoading}
              systemDefaultLanguage={scanOptsDefaults?.language || 'auto'}
            />
          ) : (
            <Button variant="outline" size="sm" className="text-xs" onClick={loadScanOptsDefaultsCard}>
              Load Scan Options Defaults
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Language Auto-Detection Settings Card (Phase 6.5) */}
      <Card data-testid="language-detect-settings-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="h-5 w-5 text-blue-500" />
                Language Auto-Detection Settings
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">Configure the OpenRouter model and prompt used when language is set to "Auto-detect".</p>
            </div>
            <Button size="sm" className="text-xs h-7" onClick={saveLangDetectSettings} disabled={savingLangDetect || !langDetectSettings} data-testid="save-lang-detect-btn">
              {savingLangDetect ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
              Save
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {langDetectSettings ? (
            <div className="space-y-4">
              {/* Model — Searchable dropdown */}
              <div>
                <Label className="text-xs font-medium">OpenRouter Model</Label>
                <p className="text-[10px] text-gray-500 mb-1">Model used for language classification (e.g. Claude 3.5 Haiku for speed/cost)</p>
                <LangDetectModelPicker
                  value={langDetectSettings.auto_detect_model || ''}
                  models={openrouterModels}
                  loading={loadingModels}
                  onChange={(modelId) => setLangDetectSettings({ ...langDetectSettings, auto_detect_model: modelId })}
                  onRetry={fetchOpenRouterModels}
                />
              </div>
              {/* Preview Characters */}
              <div>
                <Label className="text-xs font-medium">Preview Characters</Label>
                <p className="text-[10px] text-gray-500 mb-1">Number of characters sent to OpenRouter for detection (50–5000)</p>
                <Input
                  type="number"
                  min={50}
                  max={5000}
                  value={langDetectSettings.auto_detect_preview_chars || 500}
                  onChange={(e) => setLangDetectSettings({ ...langDetectSettings, auto_detect_preview_chars: parseInt(e.target.value) || 500 })}
                  className="text-xs h-8 w-32"
                  data-testid="lang-detect-preview-chars-input"
                />
              </div>
              {/* Prompt */}
              <div>
                <Label className="text-xs font-medium">Detection Prompt</Label>
                <p className="text-[10px] text-gray-500 mb-1">System prompt sent to the model. Must instruct it to return only a 2-letter ISO 639-1 code.</p>
                <textarea
                  value={langDetectSettings.auto_detect_prompt || ''}
                  onChange={(e) => setLangDetectSettings({ ...langDetectSettings, auto_detect_prompt: e.target.value })}
                  className="w-full text-xs border rounded-md p-2 h-24 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Identify the primary language..."
                  data-testid="lang-detect-prompt-textarea"
                />
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" className="text-xs" onClick={loadLangDetectSettings} data-testid="load-lang-detect-btn">
              {langDetectSettingsLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Load Language Detection Settings
            </Button>
          )}
        </CardContent>
      </Card>

      {/* KB Article Import (Zoho Desk) */}
      <Card data-testid="kb-import-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="cursor-pointer flex items-center gap-2" onClick={() => { setKbImportOpen(!kbImportOpen); if (!kbImportOpen) { fetchKbCategories(); fetchKbImportStatus(); loadScanOptsForDialog('kb', setScanOptsKb); } }}>
              <BookOpen className="h-5 w-5 text-indigo-500" />
              <CardTitle className="text-base">KB Article Import (Zoho Desk)</CardTitle>
              <span className="text-xs text-gray-400">{kbImportOpen ? '▼' : '▶'}</span>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Import knowledge base articles from Zoho Desk into the RAG system. Articles are converted to Markdown and indexed alongside uploaded documents.
          </p>
        </CardHeader>
        {kbImportOpen && (
          <CardContent className="space-y-4">
            {/* Import Progress */}
            {kbImportStatus && kbImportStatus.status !== 'not_started' && (
              <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium flex items-center gap-1.5">
                    {kbImportStatus.status === 'running' || kbImportStatus.status === 'fetching' ? (
                      <><Loader2 className="w-3 h-3 animate-spin text-indigo-500" /> {kbImportStatus.status === 'fetching' ? 'Fetching articles...' : 'Importing...'}</>
                    ) : kbImportStatus.status === 'completed' ? (
                      <><CheckCircle className="w-3 h-3 text-green-500" /> Completed</>
                    ) : (
                      <><AlertTriangle className="w-3 h-3 text-red-500" /> {kbImportStatus.status}</>
                    )}
                  </span>
                  <span className="text-gray-500">{kbImportStatus.processed || 0} / {kbImportStatus.total || '?'} articles</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-500 ${kbImportStatus.status === 'completed' ? 'bg-green-500' : kbImportStatus.status === 'failed' ? 'bg-red-500' : 'bg-indigo-500'}`}
                    style={{ width: `${kbImportStatus.total ? (kbImportStatus.processed / kbImportStatus.total * 100) : 0}%` }} />
                </div>
                <div className="flex items-center justify-between text-[10px] text-gray-500">
                  <span>Imported: {kbImportStatus.imported || 0} | Errors: {kbImportStatus.errors || 0}</span>
                  {kbImportStatus.started_at && <span>Started: {new Date(kbImportStatus.started_at).toLocaleString()}</span>}
                </div>
                {(kbImportStatus.status === 'running' || kbImportStatus.status === 'fetching') && kbImportStatus.current_article && (
                  <p className="text-[10px] text-indigo-600 truncate">Processing: {kbImportStatus.current_article}</p>
                )}
                {kbImportStatus.status === 'failed' && kbImportStatus.error && (
                  <div className="p-2 bg-red-50 rounded text-[10px] text-red-700 max-h-20 overflow-y-auto">{kbImportStatus.error}</div>
                )}
              </div>
            )}

            {/* Category Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-medium text-sm">Categories & Articles</Label>
                {loadingKbCats && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
              </div>
              {kbCategories.length > 0 ? (
                <>
                  <input
                    type="text"
                    value={kbCatSearch}
                    onChange={(e) => setKbCatSearch(e.target.value)}
                    placeholder="Search categories..."
                    className="w-full text-xs px-2 py-1.5 border rounded-md"
                    data-testid="kb-category-search"
                  />
                  <div className="max-h-72 overflow-y-auto border rounded-lg p-1.5 space-y-0.5" data-testid="kb-category-tree">
                    {kbCategories
                      .filter(cat => cat.name.toLowerCase().includes(kbCatSearch.toLowerCase()))
                      .map((cat) => {
                        const expanded = kbExpanded.includes(cat.id);
                        const arts = kbArticlesByCat[cat.id] || [];
                        const loadingArts = kbLoadingArts.includes(cat.id);
                        const catChecked = kbSelectedCats.includes(cat.id);
                        return (
                          <div key={cat.id} data-testid={`kb-category-row-${cat.id}`}>
                            <div className="flex items-center gap-1.5 text-xs p-1 hover:bg-gray-50 rounded">
                              <button
                                type="button"
                                onClick={() => toggleKbExpand(cat.id)}
                                className="w-4 text-gray-400 hover:text-gray-700"
                                data-testid={`kb-category-expand-${cat.id}`}>
                                {expanded ? '▼' : '▶'}
                              </button>
                              <input type="checkbox" className="rounded"
                                checked={catChecked}
                                onChange={() => toggleKbCategory(cat.id)}
                                data-testid={`kb-category-checkbox-${cat.id}`}
                              />
                              <span className="flex-1 font-medium text-gray-700 cursor-pointer" onClick={() => toggleKbExpand(cat.id)}>
                                {cat.name}
                              </span>
                            </div>
                            {expanded && (
                              <div className="ml-7 border-l pl-2 py-0.5 space-y-0.5">
                                {loadingArts ? (
                                  <p className="text-[10px] text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading articles...</p>
                                ) : arts.length > 0 ? (
                                  arts.map((art) => (
                                    <label key={art.id} className="flex items-center gap-1.5 text-[11px] p-0.5 hover:bg-gray-50 rounded cursor-pointer" data-testid={`kb-article-row-${art.id}`}>
                                      <input type="checkbox" className="rounded"
                                        disabled={catChecked}
                                        checked={catChecked || kbSelectedArticles.includes(art.id)}
                                        onChange={() => toggleKbArticle(art.id)}
                                        data-testid={`kb-article-checkbox-${art.id}`}
                                      />
                                      <span className="flex-1 text-gray-600 truncate" title={art.title}>{art.title}</span>
                                      {art.section && <Badge className="bg-gray-100 text-gray-500 text-[9px]">{art.section}</Badge>}
                                    </label>
                                  ))
                                ) : (
                                  <p className="text-[10px] text-gray-400">No published articles in this category.</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  <p className="text-[10px] text-indigo-600" data-testid="kb-import-selection-summary">
                    {kbSelectedCats.length === 0 && kbSelectedArticles.length === 0
                      ? 'Nothing selected — import will include ALL Published articles.'
                      : `Selected: ${kbSelectedCats.length} categor${kbSelectedCats.length === 1 ? 'y' : 'ies'} + ${kbSelectedArticles.length} article${kbSelectedArticles.length === 1 ? '' : 's'}.`}
                  </p>
                </>
              ) : (
                <p className="text-xs text-gray-400">{loadingKbCats ? 'Loading categories...' : 'Click to load categories from Zoho'}</p>
              )}
              <p className="text-[10px] text-gray-400">Check a category to import everything under it, or expand it to pick individual articles. Only Published articles are imported.</p>
            </div>

            {/* Scan Options for KB Import */}
            <ScanOptionsPanel
              value={scanOptsKb}
              onChange={(v) => updateScanOpts('kb', setScanOptsKb, v)}
              onResetToDefaults={() => resetScanOptsForDialog('kb', setScanOptsKb)}
              collapsible={true}
              defaultExpanded={false}
            />

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button variant="default" size="sm" className="text-xs h-7 bg-indigo-600 hover:bg-indigo-700" onClick={handleStartKbImport}
                disabled={kbImportStatus?.status === 'running' || kbImportStatus?.status === 'fetching'} data-testid="start-kb-import-btn">
                <BookOpen className="h-3 w-3 mr-1" />
                {kbImportStatus?.status === 'running' ? 'Importing...' : 'Import Articles'}
              </Button>
              {(kbImportStatus?.status === 'running' || kbImportStatus?.status === 'fetching') && (
                <Button variant="destructive" size="sm" className="text-xs h-7" onClick={handleStopKbImport} data-testid="stop-kb-import-btn">
                  <XCircle className="h-3 w-3 mr-1" /> Stop Import
                </Button>
              )}
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={fetchKbImportStatus}>Refresh Status</Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* URL Import */}
      <Card data-testid="url-import-card">
        <CardHeader>
          <div className="cursor-pointer flex items-center gap-2" onClick={() => { if (!urlImportOpen) loadScanOptsForDialog('url', setScanOptsUrl); setUrlImportOpen(!urlImportOpen); }}>
            <Link2 className="h-5 w-5 text-teal-500" />
            <CardTitle className="text-base">Website/URL Import</CardTitle>
            <span className="text-xs text-gray-400">{urlImportOpen ? '▼' : '▶'}</span>
          </div>
          <p className="text-xs text-gray-500">Import content from any public website or URL via DocuLing.</p>
        </CardHeader>
        {urlImportOpen && (
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://docs.example.com" className="flex-1 text-xs" data-testid="url-input" />
              <Button size="sm" className="text-xs h-8" onClick={handleScanUrl} disabled={scanningUrl}>
                {scanningUrl ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
                Scan for Pages
              </Button>
              <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => handleImportUrls([urlInput.trim()])} disabled={!urlInput.trim()}>
                Import Single URL
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 shrink-0">Collection:</span>
              <Input value={urlCollectionName} onChange={(e) => setUrlCollectionName(e.target.value)} placeholder="URLs" className="w-48 text-xs h-7" data-testid="url-collection-input" />
            </div>

            {/* Scan Options for URL Import */}
            <ScanOptionsPanel
              value={scanOptsUrl}
              onChange={(v) => updateScanOpts('url', setScanOptsUrl, v)}
              onResetToDefaults={() => resetScanOptsForDialog('url', setScanOptsUrl)}
              collapsible={true}
              defaultExpanded={false}
            />

            {scannedPages.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium">Discovered Pages ({scannedPages.length})</p>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="text-[10px] h-5" onClick={() => setSelectedUrls(scannedPages.map(p => p.url))}>Select All</Button>
                    <Button variant="ghost" size="sm" className="text-[10px] h-5" onClick={() => setSelectedUrls([])}>Deselect</Button>
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {scannedPages.map((p, i) => (
                    <label key={i} className="flex items-center gap-2 text-xs p-0.5 hover:bg-gray-50 rounded cursor-pointer">
                      <input type="checkbox" className="rounded" checked={selectedUrls.includes(p.url)}
                        onChange={(e) => { if (e.target.checked) setSelectedUrls(prev => [...prev, p.url]); else setSelectedUrls(prev => prev.filter(u => u !== p.url)); }} />
                      <span className="truncate">{p.url}</span>
                    </label>
                  ))}
                </div>
                <Button size="sm" className="text-xs h-7 bg-teal-600 hover:bg-teal-700" onClick={() => handleImportUrls(selectedUrls)} disabled={selectedUrls.length === 0}>
                  Import Selected ({selectedUrls.length})
                </Button>
              </div>
            )}

            {urlImportStatus && urlImportStatus.status !== 'not_started' && (
              <div className="p-2 bg-gray-50 rounded space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{urlImportStatus.status}</span>
                  <span>{urlImportStatus.processed}/{urlImportStatus.total} | Imported: {urlImportStatus.imported} | Errors: {urlImportStatus.errors}</span>
                </div>
                {/* Media enrichment progress */}
                {urlImportStatus.enrichment_status && urlImportStatus.enrichment_status !== 'done' && (
                  <div className="flex items-center gap-2 text-[10px] text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>
                      {urlImportStatus.enrichment_status === 'extracting' && 'Extracting media from page...'}
                      {urlImportStatus.enrichment_status === 'indexing_children' && (
                        `Indexing media: ${urlImportStatus.media_indexed || 0}/${urlImportStatus.media_total || '?'} items`
                      )}
                    </span>
                  </div>
                )}
                {urlImportStatus.media_found && Object.keys(urlImportStatus.media_found).length > 0 && (
                  <div className="flex items-center gap-2 text-[10px] text-gray-600">
                    <span>Media found:</span>
                    {urlImportStatus.media_found.image && <span>🖼️ {urlImportStatus.media_found.image}</span>}
                    {urlImportStatus.media_found.video && <span>🎥 {urlImportStatus.media_found.video}</span>}
                    {urlImportStatus.media_found.audio && <span>🎵 {urlImportStatus.media_found.audio}</span>}
                    {urlImportStatus.media_found.pdf_link && <span>📎 {urlImportStatus.media_found.pdf_link}</span>}
                    {urlImportStatus.media_found.external_url && <span>🔗 {urlImportStatus.media_found.external_url}</span>}
                  </div>
                )}
                {urlImportStatus.failed_urls?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-medium text-red-600">Failed URLs ({urlImportStatus.failed_urls.length}):</p>
                    <div className="max-h-32 overflow-y-auto space-y-0.5">
                      {urlImportStatus.failed_urls.map((f, i) => (
                        <div key={i} className="flex items-center justify-between bg-red-50 px-2 py-0.5 rounded text-[10px]">
                          <span className="truncate flex-1 text-red-700">{f.url}</span>
                          <span className="text-red-500 ml-2 shrink-0">{f.error?.substring(0, 40)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <Button variant="ghost" size="sm" className="text-[10px] h-5" onClick={fetchUrlImportStatus}>Refresh</Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Document Summaries (3-Hop Search) */}
      <Card data-testid="summary-settings-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="cursor-pointer flex items-center gap-2" onClick={() => { setSummarySettingsOpen(!summarySettingsOpen); if (!summarySettingsOpen) fetchSummaryBulkStatus(); }}>
              <Sparkles className="h-5 w-5 text-amber-500" />
              <CardTitle className="text-base">Document Summaries (3-Hop Search)</CardTitle>
              <Badge className={summarySettings.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                {summarySettings.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <span className="text-xs text-gray-400">{summarySettingsOpen ? '▼' : '▶'}</span>
            </div>
            {summarySettingsOpen && (
              <Button size="sm" className="text-xs h-7" onClick={handleSaveSummarySettings} disabled={savingSummary} data-testid="save-summary-settings-btn">
                <Save className="h-3 w-3 mr-1" />{savingSummary ? 'Saving...' : 'Save Settings'}
              </Button>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Generates LLM summaries per document and section for hierarchical 3-hop search.
            {summaryStats && ` ${summaryStats.doc_summaries_generated}/${summaryStats.total_documents} docs, ${summaryStats.section_summaries_generated}/${summaryStats.total_sections} sections summarized.`}
          </p>
        </CardHeader>
        {summarySettingsOpen && (
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">Enable Summary Generation</Label>
                <p className="text-xs text-gray-500 mt-0.5">When enabled, new documents will get LLM-generated summaries automatically.</p>
              </div>
              <Switch checked={summarySettings.enabled} onCheckedChange={(v) => setSummarySettings(p => ({ ...p, enabled: v }))} data-testid="summary-toggle" />
            </div>

            <div className="space-y-2">
              <Label className="font-medium text-sm">Summarization Model</Label>
              <ModelCombobox
                idx="summary"
                label=""
                value={summarySettings.model}
                models={openrouterModels}
                defaultFilter="file"
                onChange={(val) => setSummarySettings(p => ({ ...p, model: val }))}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-medium text-sm">Document Summary Prompt</Label>
                <Button variant="ghost" size="sm" className="text-xs h-6" onClick={async () => {
                  const res = await authedFetch(`${API_URL}/api/rag/cdm/summary-settings/reset-prompts`, { method: 'POST' });
                  if (res.ok) { const d = await res.json(); setSummarySettings(p => ({ ...p, doc_prompt: d.doc_prompt, section_prompt: d.section_prompt })); toast.success('Prompts reset'); }
                }}>Reset to Default</Button>
              </div>
              <textarea className="w-full min-h-[80px] p-3 text-xs border border-gray-200 rounded-lg font-mono resize-y focus:outline-none focus:ring-2 focus:ring-amber-200"
                value={summarySettings.doc_prompt} onChange={(e) => setSummarySettings(p => ({ ...p, doc_prompt: e.target.value }))} data-testid="doc-summary-prompt" />
            </div>

            <div className="space-y-2">
              <Label className="font-medium text-sm">Section Summary Prompt</Label>
              <textarea className="w-full min-h-[80px] p-3 text-xs border border-gray-200 rounded-lg font-mono resize-y focus:outline-none focus:ring-2 focus:ring-amber-200"
                value={summarySettings.section_prompt} onChange={(e) => setSummarySettings(p => ({ ...p, section_prompt: e.target.value }))} data-testid="section-summary-prompt" />
            </div>

            <div className="space-y-4 pt-2 border-t">
              {/* Generation Progress */}
              {summaryBulkStatus?.job && summaryBulkStatus.job.status !== 'not_started' && (
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium flex items-center gap-1.5">
                      {summaryBulkStatus.job.status === 'running' ? (
                        <><Loader2 className="w-3 h-3 animate-spin text-amber-500" /> Running</>
                      ) : summaryBulkStatus.job.status === 'completed' ? (
                        <><CheckCircle className="w-3 h-3 text-green-500" /> Completed</>
                      ) : (
                        <><AlertTriangle className="w-3 h-3 text-red-500" /> {summaryBulkStatus.job.status}</>
                      )}
                    </span>
                    <span className="text-gray-500">{summaryBulkStatus.job.processed || 0} / {summaryBulkStatus.job.total || 0} documents</span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        summaryBulkStatus.job.status === 'completed' ? 'bg-green-500' :
                        summaryBulkStatus.job.status === 'failed' ? 'bg-red-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${summaryBulkStatus.job.total ? (summaryBulkStatus.job.processed / summaryBulkStatus.job.total * 100) : 0}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-gray-500">
                    <span>Doc summaries: {summaryBulkStatus.job.doc_summaries || 0} | Section: {summaryBulkStatus.job.section_summaries || 0} | Errors: {summaryBulkStatus.job.errors || 0}</span>
                    {summaryBulkStatus.job.started_at && <span>Started: {new Date(summaryBulkStatus.job.started_at).toLocaleString()}</span>}
                  </div>
                  {/* Current document being processed */}
                  {summaryBulkStatus.job.status === 'running' && summaryBulkStatus.job.current_document && (
                    <p className="text-[10px] text-amber-600 truncate">Processing: {summaryBulkStatus.job.current_document}</p>
                  )}
                  {/* Error message if failed */}
                  {summaryBulkStatus.job.status === 'failed' && summaryBulkStatus.job.error && (
                    <div className="p-2 bg-red-50 rounded text-[10px] text-red-700 max-h-20 overflow-y-auto">
                      {summaryBulkStatus.job.error}
                    </div>
                  )}
                </div>
              )}

              {/* Per-Document Summary Status Table */}
              {summaryBulkStatus?.documents && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-500">Per-Document Status</p>
                    <Input
                      className="w-48 h-7 text-xs"
                      placeholder="Search documents..."
                      value={summaryDocSearch}
                      onChange={(e) => setSummaryDocSearch(e.target.value)}
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="text-left text-gray-500">
                          <th className="px-3 py-1.5">Document</th>
                          <th className="px-3 py-1.5 w-24">Status</th>
                          <th className="px-3 py-1.5 w-36">Generated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryBulkStatus.documents
                          .filter(d => !summaryDocSearch || d.title.toLowerCase().includes(summaryDocSearch.toLowerCase()))
                          .map((d) => (
                          <tr key={d.doc_id} className="border-t">
                            <td className="px-3 py-1.5 truncate max-w-xs" title={d.title}>{d.title}</td>
                            <td className="px-3 py-1.5">
                              {d.status === 'new' ? (
                                <span className="flex items-center gap-1 text-green-700"><CheckCircle className="w-3 h-3" /> New</span>
                              ) : d.status === 'old' ? (
                                <span className="flex items-center gap-1 text-amber-600"><Clock className="w-3 h-3" /> Old</span>
                              ) : (
                                <span className="flex items-center gap-1 text-gray-400"><Loader2 className="w-3 h-3" /> Pending</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-gray-400">
                              {d.generated_at ? new Date(d.generated_at).toLocaleString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => { handleGenerateAllSummaries(); }} disabled={generatingAllSummaries || summaryBulkStatus?.job?.status === 'running'} data-testid="generate-all-summaries-btn">
                  <Sparkles className="h-3 w-3 mr-1" />{summaryBulkStatus?.job?.status === 'running' ? 'Running...' : generatingAllSummaries ? 'Starting...' : 'Generate All Summaries'}
                </Button>
                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={fetchSummaryBulkStatus} data-testid="refresh-summary-status-btn">
                  Refresh Status
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* RAG Rules — Configurable from CDM Documents */}
      <Card data-testid="rag-rules-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="cursor-pointer flex items-center gap-2" onClick={() => setRagRulesOpen(!ragRulesOpen)}>
              <CardTitle className="text-base">Knowledge Base Rules</CardTitle>
              <span className="text-xs text-gray-400">{ragRulesOpen ? '▼' : '▶'}</span>
            </div>
            {ragRulesOpen && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={handleResetRagRules}>Reset Defaults</Button>
                <Button size="sm" className="text-xs h-7" onClick={handleSaveRagRules} disabled={savingRules}>
                  {savingRules ? '...' : 'Save Rules'}
                </Button>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Rules that tell the AI how to use retrieved documents. Controls anti-mixing, anti-hallucination, and completeness of responses.
          </p>
        </CardHeader>
        {ragRulesOpen && (
          <CardContent>
            <textarea
              value={ragRules}
              onChange={(e) => setRagRules(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 text-xs font-mono border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter RAG rules..."
            />
          </CardContent>
        )}
      </Card>

      {/* Documents List */}
      <Card data-testid="documents-list-card">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-lg">Documents</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={collectionFilter || 'all'} onValueChange={setCollectionFilter}>
                <SelectTrigger className="w-44" data-testid="collection-filter">
                  <SelectValue placeholder="Filter by collection" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Collections</SelectItem>
                  {collections.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="structured">Structured</SelectItem>
                  <SelectItem value="chunked">Chunked</SelectItem>
                  <SelectItem value="embedded">Embedded</SelectItem>
                  <SelectItem value="indexed">Indexed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={parentFilter} onValueChange={(v) => { setParentFilter(v); setPage(1); }}>
                <SelectTrigger className="w-40" data-testid="parent-filter">
                  <SelectValue placeholder="Parent/Child" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Documents</SelectItem>
                  <SelectItem value="parents_only">Parents Only</SelectItem>
                  <SelectItem value="children_only">Children Only</SelectItem>
                </SelectContent>
              </Select>
              <Select value={reviewFilter} onValueChange={(v) => { setReviewFilter(v); setPage(1); }}>
                <SelectTrigger className="w-36" data-testid="review-filter">
                  <SelectValue placeholder="Review Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reviews</SelectItem>
                  <SelectItem value="unreviewed">Unreviewed</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="edited">Edited</SelectItem>
                </SelectContent>
              </Select>
              <Select value={embeddingFilter} onValueChange={(v) => { setEmbeddingFilter(v); setPage(1); }}>
                <SelectTrigger className="w-44" data-testid="embedding-filter">
                  <SelectValue placeholder="Embedding Health" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Embeddings</SelectItem>
                  <SelectItem value="problems">⚠ Embedding problems</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                disabled={reindexing}
                data-testid="reindex-collections-btn"
                onClick={async () => {
                  if (!confirm("Re-index all documents for collection filtering? This sets indexed documents back to 'chunked' status for re-processing by the embedding worker.")) return;
                  setReindexing(true);
                  try {
                    const token = localStorage.getItem("token");
                    const res = await authedFetch(`${API_URL}/api/rag/cdm/maintenance/reindex-collections`, {
                      method: 'POST',
                      headers: token ? { Authorization: `Bearer ${token}` } : {},
                    });
                    const data = await res.json();
                    if (data.success) {
                      toast.success(data.message);
                      setTimeout(() => { fetchDocuments(); fetchSystemStats(); }, 2000);
                    } else {
                      toast.error(data.detail || 'Re-index failed');
                    }
                  } catch (e) {
                    toast.error('Re-index failed: ' + e.message);
                  } finally {
                    setReindexing(false);
                  }
                }}
              >
                {reindexing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                {reindexing ? "Re-indexing..." : "Re-index for Collections"}
              </Button>
              {((systemStats?.documents_by_status?.structured || 0) > 0 || (systemStats?.documents_by_status?.chunked || 0) > 0) && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                disabled={processingAll}
                data-testid="process-all-pending-btn"
                onClick={async () => {
                  const structured = systemStats?.documents_by_status?.structured || 0;
                  const chunked = systemStats?.documents_by_status?.chunked || 0;
                  const total = structured + chunked;
                  if (total === 0) {
                    toast.info("No pending documents to process.");
                    return;
                  }
                  if (!confirm(`Publish ${total} documents to Kafka for processing?\n\n${structured > 0 ? `${structured} structured → chunking → embedding → indexing\n` : ''}${chunked > 0 ? `${chunked} chunked → embedding → indexing` : ''}\n\nWorkers will process them automatically.`)) return;
                  setProcessingAll(true);
                  try {
                    const res = await authedFetch(`${API_URL}/api/rag/cdm/maintenance/process-all-pending`, { method: 'POST' });
                    const data = await res.json();
                    if (data.success) {
                      let msg = data.message;
                      if (data.warnings && data.warnings.length > 0) {
                        toast.warning(msg + " Warning: " + data.warnings[0]);
                      } else {
                        toast.success(msg);
                      }
                    } else {
                      toast.error(data.detail || data.message || 'Failed to process');
                    }
                    setTimeout(() => { fetchDocuments(); fetchSystemStats(); }, 3000);
                  } catch (e) {
                    toast.error('Failed: ' + e.message);
                  } finally {
                    setProcessingAll(false);
                  }
                }}
              >
                {processingAll ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Activity className="h-3 w-3 mr-1" />}
                {processingAll ? "Publishing..." : "Process All Pending"}
              </Button>
              )}
            </div>
            {/* Re-embed Chunked + Delete All Buttons */}
            <div className="flex items-center gap-2 ml-auto">
              {systemStats?.documents_by_status?.chunked > 0 && (
                <Button variant="outline" size="sm" className="text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={async () => {
                    try {
                      const res = await authedFetch(`${API_URL}/api/rag/cdm/maintenance/re-embed-chunked`, { method: 'POST' });
                      if (res.ok) toast.success('Re-embedding chunked documents...');
                      else toast.error('Failed');
                    } catch (e) { toast.error('Failed'); }
                  }} data-testid="re-embed-btn">
                  Re-embed {systemStats.documents_by_status.chunked} Chunked
                </Button>
              )}
              <Button variant="outline" size="sm" className="text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                onClick={handleDeleteAll} data-testid="delete-all-btn">
                <Trash2 className="h-3 w-3 mr-1" /> Delete All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search bar (Phase 9.5.1: search-on-submit, not on keystroke) */}
          <div className="mb-4">
            <div className="relative flex gap-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by title, source URL, or content... (press Enter)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') executeSearch(); }}
                  className="pl-9 pr-8 h-9 text-sm"
                  data-testid="document-search-input"
                />
                {(searchInput || searchQuery) && (
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={clearSearch}
                    data-testid="search-clear-btn"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-9 px-3"
                onClick={executeSearch}
                data-testid="search-submit-btn"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Tensornex Sync */}
          {orphanData && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-2" data-testid="orphan-panel">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium">Tensornex Sync: {orphanData.mongo_docs} docs in MongoDB, ~{orphanData.qdrant_docs_sampled} in Qdrant</span>
                <div className="flex gap-1">
                  {orphanData.orphan_count > 0 && (
                    <Button variant="destructive" size="sm" className="text-[10px] h-6" onClick={handleDeleteOrphans} disabled={deletingOrphans}>
                      {deletingOrphans ? 'Deleting...' : `Delete ${orphanData.orphan_count} Orphans`}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-[10px] h-6" onClick={handleCheckOrphans}>Recheck</Button>
                </div>
              </div>
              {orphanData.orphan_count > 0 && (
                <div className="text-[10px] text-red-600">
                  <p className="font-medium">Orphan vectors (in Qdrant, not in MongoDB):</p>
                  {orphanData.orphans_in_qdrant.slice(0, 10).map((o, i) => (
                    <div key={i} className="truncate">{o.doc_id}</div>
                  ))}
                  {orphanData.orphan_count > 10 && <p>...and {orphanData.orphan_count - 10} more</p>}
                </div>
              )}
              {orphanData.missing_count > 0 && (
                <div className="text-[10px] text-amber-600">
                  <p className="font-medium">Indexed but missing vectors ({orphanData.missing_count}):</p>
                  {orphanData.missing_vectors.slice(0, 5).map((m, i) => (
                    <div key={i} className="truncate">{m.title}</div>
                  ))}
                </div>
              )}
              {orphanData.orphan_count === 0 && orphanData.missing_count === 0 && (
                <p className="text-[10px] text-green-600">All clean — no orphans found.</p>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mb-3">
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={handleCheckOrphans} disabled={checkingOrphans} data-testid="check-orphans-btn">
              {checkingOrphans ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
              Check Tensornex Sync
            </Button>
          </div>

          {documents.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="no-documents">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No documents found</p>
              <p className="text-sm mt-1">Documents will appear here after ingestion</p>
            </div>
          ) : (
            <>
              {/* Bulk Actions Bar */}
              {selectedDocIds.length > 0 && (
                <div className="flex items-center gap-2 p-2 mb-2 bg-blue-50 rounded-lg border border-blue-200" data-testid="bulk-actions">
                  <span className="text-xs font-medium text-blue-700">{selectedDocIds.length} selected</span>
                  <Select value={bulkType} onValueChange={setBulkType}>
                    <SelectTrigger className="w-28 h-7 text-xs"><SelectValue placeholder="Set Type..." /></SelectTrigger>
                    <SelectContent>
                      {['article', 'pdf', 'url', 'video', 'audio', 'image', 'text', 'docx'].map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={bulkCollection} onChange={(e) => setBulkCollection(e.target.value)} placeholder="Set Collection..." className="w-36 h-7 text-xs" />
                  <Button size="sm" className="text-xs h-7" onClick={handleBulkUpdate} disabled={!bulkType && !bulkCollection}>Apply</Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-7 border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={handleBulkReembed}
                    disabled={!!bulkReembedProgress}
                    data-testid="bulk-reembed-btn"
                  >
                    {bulkReembedProgress
                      ? <><Loader2 className="h-3 w-3 mr-1 animate-spin inline" />Re-embedding {bulkReembedProgress.current}/{bulkReembedProgress.total}</>
                      : <><RefreshCw className="h-3 w-3 mr-1 inline" />Re-embed selected</>}
                  </Button>
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setSelectedDocIds([])}>Cancel</Button>
                </div>
              )}
              <div className="overflow-x-auto w-0 min-w-full">
                <table className="w-full" data-testid="documents-table">
                  <thead>
                    <tr className="border-b">
                      <th className="py-3 px-2 w-8">
                        <input type="checkbox" className="rounded"
                          checked={selectedDocIds.length === documents.length && documents.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedDocIds(documents.map(d => d.doc_id));
                            else setSelectedDocIds([]);
                          }} />
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Title / Source</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Type</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Source</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Language</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Parent</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Collection</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Enrichment</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Review</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Ingested</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => {
                      const StatusIcon = STATUS_ICONS[doc.processing_status] || AlertCircle;
                      const isParent = !doc.parent_doc_id;
                      const isChild = !!doc.parent_doc_id;
                      const isCollapsed = collapsedParents.has(doc.doc_id);

                      {/* Phase 9.5: Hide children of collapsed parents.
                          Known UX tradeoff: collapsing hides rows visually but doesn't recalculate
                          pagination. May leave empty space on the page. Acceptable for Phase 9.5;
                          dynamic pagination recalculation deferred to future improvement. */}
                      if (isChild && collapsedParents.has(doc.parent_doc_id)) {
                        return null;
                      }

                      return (
                        <tr
                          key={doc.doc_id}
                          className={`border-b hover:bg-gray-50 cursor-pointer ${isChild ? 'bg-gray-50/50' : ''}`}
                          onClick={() => handleViewDocument(doc)}
                          data-testid={`doc-row-${doc.doc_id}`}
                        >
                          <td className="py-3 px-2 w-8" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" className="rounded"
                              checked={selectedDocIds.includes(doc.doc_id)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedDocIds(prev => [...prev, doc.doc_id]);
                                else setSelectedDocIds(prev => prev.filter(id => id !== doc.doc_id));
                              }} />
                          </td>
                          <td className={`py-3 px-4 ${isChild ? 'pl-12' : ''}`}>
                            <div className="flex items-start gap-1">
                              {isParent && parentFilter !== 'parents_only' && doc.children_count > 0 && (
                                <button
                                  className="mt-0.5 shrink-0 text-gray-400 hover:text-gray-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCollapsedParents(prev => {
                                      const next = new Set(prev);
                                      if (next.has(doc.doc_id)) next.delete(doc.doc_id);
                                      else next.add(doc.doc_id);
                                      return next;
                                    });
                                  }}
                                  data-testid={`chevron-${doc.doc_id}`}
                                >
                                  {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </button>
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900">
                                  {doc.embedding_health && (
                                    <span
                                      className={`inline-block w-2 h-2 rounded-full mr-1.5 align-middle shrink-0 ${
                                        doc.embedding_health === 'ok' ? 'bg-green-500' :
                                        doc.embedding_health === 'stale' ? 'bg-amber-500' :
                                        'bg-red-500'
                                      }`}
                                      title={
                                        doc.embedding_health === 'ok' ? 'Embeddings are on the active model' :
                                        doc.embedding_health === 'stale' ? 'On a different embedding model — re-embed recommended' :
                                        'Missing embeddings — re-embed required (unsearchable)'
                                      }
                                      data-testid={`embedding-health-dot-${doc.doc_id}`}
                                    />
                                  )}
                                  {doc.title || truncateText(doc.source_uri, 50)}
                                  {isParent && doc.children_count > 0 && (
                                    <span className="text-xs text-gray-400 ml-1 font-normal">({doc.children_count} children)</span>
                                  )}
                                </p>
                                {doc.title && (
                                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                                    {truncateText(doc.source_uri, 40)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={DOC_TYPE_COLORS[doc.doc_type || (doc.source_type === 'zoho_kb_article' ? 'article' : 'text')] || 'bg-gray-100'}>
                              {doc.doc_type || (doc.source_type === 'zoho_kb_article' ? 'article' : 'unknown')}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-[10px] text-gray-500">
                              {doc.source_type === 'zoho_kb_article' ? '📚 Zoho' : doc.source_type === 'url' || doc.doc_type === 'url' ? '🌐 URL' : '📁 Upload'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {(() => {
                              const ld = getLangDisplay(doc.language);
                              return (
                                <Badge
                                  className={`text-[10px] ${doc.language ? 'bg-sky-50 text-sky-700' : 'bg-gray-50 text-gray-400'}`}
                                  data-testid={`lang-badge-${doc.doc_id}`}
                                >
                                  {ld.flag} {ld.code ? ld.code.toUpperCase() : 'Unknown'}{doc.language_inherited ? ' (inherited)' : ''}
                                </Badge>
                              );
                            })()}
                          </td>
                          <td className="py-3 px-4" data-testid={`parent-cell-${doc.doc_id}`}>
                            {doc.parent_doc_id ? (
                              <button
                                className="text-xs text-blue-600 hover:underline truncate max-w-[140px] text-left"
                                onClick={(e) => { e.stopPropagation(); handleViewDocument({ doc_id: doc.parent_doc_id, title: doc.parent_title }); }}
                                title={doc.parent_title || doc.parent_doc_id}
                                data-testid={`parent-link-${doc.doc_id}`}
                              >
                                {doc.parent_title || 'Parent'}
                              </button>
                            ) : (
                              <span className="text-[10px] text-gray-300">&mdash;</span>
                            )}
                          </td>
                          <td className="py-3 px-4" data-testid={`collection-cell-${doc.doc_id}`}>
                            <Badge className={
                              (doc.collection_id && doc.collection_id !== "General")
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-600"
                            }>
                              {doc.collection_id || "General"}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <StatusIcon
                                className={`h-4 w-4 ${doc.processing_status === 'processing' ? 'animate-spin' : ''}`}
                              />
                              <Badge className={STATUS_COLORS[doc.processing_status]}>
                                {doc.processing_status}
                              </Badge>
                              {(() => {
                                const gs = graphStatusBulk[doc.doc_id];
                                if (!gs) return <span className="w-2 h-2 rounded-full bg-gray-300 shrink-0" title="Graph: not indexed" />;
                                if (gs.status === 'complete' && gs.entities_indexed > 0) return <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" title={`Graph: ${gs.entities_indexed} entities`} />;
                                if (gs.status === 'complete') return <span className="w-2 h-2 rounded-full bg-yellow-400 shrink-0" title="Graph: linked, no entities" />;
                                return <span className="w-2 h-2 rounded-full bg-red-400 shrink-0" title="Graph: failed" />;
                              })()}
                            </div>
                          </td>
                          <td className="py-3 px-4" data-testid={`enrichment-cell-${doc.doc_id}`}>
                            {(() => {
                              const es = doc.enrichment_status;
                              const isParent = !doc.parent_doc_id;
                              if (!isParent) return <span className="text-[10px] text-gray-300">&mdash;</span>;
                              if (!es) return <span className="text-[10px] text-gray-300">&mdash;</span>;
                              if (es.state === 'skipped') return <Badge className="bg-gray-100 text-gray-500 text-[10px]">Skipped</Badge>;
                              if (es.state === 'completed') {
                                const created = es.children_created || 0;
                                const failed = es.children_failed || 0;
                                if (created === 0 && failed === 0) return <Badge className="bg-gray-100 text-gray-500 text-[10px]">None</Badge>;
                                return (
                                  <Badge className="bg-green-100 text-green-700 text-[10px]" title={`${created} created, ${failed} failed`}>
                                    {created} child{created !== 1 ? 'ren' : ''}
                                    {failed > 0 && <span className="text-red-500 ml-0.5">({failed} err)</span>}
                                  </Badge>
                                );
                              }
                              if (es.state === 'in_progress') {
                                const processed = es.media_processed || 0;
                                const found = es.media_found || 0;
                                return (
                                  <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                                    <Loader2 className="w-3 h-3 animate-spin mr-1 inline" />
                                    {processed}/{found}
                                  </Badge>
                                );
                              }
                              if (es.state === 'pending') return <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Clock className="w-3 h-3 mr-1 inline" />Pending</Badge>;
                              if (es.state === 'failed') return <Badge className="bg-red-100 text-red-700 text-[10px]">Failed</Badge>;
                              return <span className="text-[10px] text-gray-300">&mdash;</span>;
                            })()}
                          </td>
                          <td className="py-3 px-4" data-testid={`review-cell-${doc.doc_id}`}>
                            {(() => {
                              const rs = doc.review_status || 'auto_extracted';
                              if (rs === 'reviewed') return <Badge className="bg-green-100 text-green-700 text-[10px]" title={doc.reviewed_at ? `Reviewed ${doc.reviewed_at}` : ''}>Reviewed</Badge>;
                              if (rs === 'edited') return <Badge className="bg-blue-100 text-blue-700 text-[10px]" title={doc.reviewed_at ? `Edited ${doc.reviewed_at}` : ''}>Edited</Badge>;
                              return <Badge className="bg-gray-100 text-gray-500 text-[10px]">Auto</Badge>;
                            })()}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {formatDate(doc.ingested_at)}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              {doc.embedding_health && doc.embedding_health !== 'ok' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReembedDoc(doc.doc_id);
                                  }}
                                  disabled={reembeddingDocIds.includes(doc.doc_id)}
                                  className={doc.embedding_health === 'missing' ? 'text-red-500 hover:text-red-700' : 'text-amber-600 hover:text-amber-700'}
                                  title={doc.embedding_health === 'missing' ? 'Re-embed (missing embeddings)' : 'Re-embed (different model)'}
                                  data-testid={`reembed-doc-${doc.doc_id}`}
                                >
                                  {reembeddingDocIds.includes(doc.doc_id)
                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                    : <RefreshCw className="h-4 w-4" />}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDocument(doc);
                                }}
                                data-testid={`view-doc-${doc.doc_id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteDocument(doc.doc_id);
                                }}
                                className="text-red-500 hover:text-red-700"
                                data-testid={`delete-doc-${doc.doc_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalDocs)} of {totalDocs}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Document Detail Dialog */}
      <DocumentDetailDialog {...{
  activeTab, aiDetectionResult, chunkSaveSuccess, detailLoading, docChunks, docDetail,
  docNodes, docStats, docSummaryStatus, editingChunk, editingChunkText, editingDocSummary,
  editingDocSummaryText, editingSectionIdx, editingSectionText, editingSectionTitle, fetchDocSummaryStatus, fetchDocuments,
  fetchSystemStats, handleDeleteDocument, savingChunk, savingDocSummary, savingSectionIdx, sectionSaveSuccess,
  selectedDoc, setActiveTab, setChunkSaveSuccess, setDocChunks, setDocDetail, setEditingChunk,
  setEditingChunkText, setEditingDocSummary, setEditingDocSummaryText, setEditingSectionIdx, setEditingSectionText, setEditingSectionTitle,
  setSavingChunk, setSavingDocSummary, setSavingSectionIdx, setSectionSaveSuccess, setSelectedDoc, startStatusPolling,
  tensornexStatus, totalChunks, totalNodes,
      }} />

      {/* Upload Document Dialog */}
      <UploadDocumentDialog {...{
  collections, dragOver, handleFileSelect, handleUpload, newCollectionName, parentDocsForReplace,
  removeUploadFile, replaceMode, replaceTargetDoc, resetScanOptsForDialog, scanOptsUpload, setDragOver,
  setNewCollectionName, setParentDocsForReplace, setReplaceMode, setReplaceTargetDoc, setScanOptsUpload, setShowNewCollection,
  setShowUploadDialog, setUploadCancelled, setUploadCollectionId, showNewCollection, showUploadDialog, systemCapacity,
  updateScanOpts, uploadCancelledRef, uploadCollectionId, uploadError, uploadFiles, uploadProgress,
  uploading,
      }} />

      {/* How It Works Guide Dialog */}
      {/* Pipeline Config Dialog */}
      <Dialog open={showPipelineConfig} onOpenChange={setShowPipelineConfig}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-cyan-600" />
              Processing Pipeline Settings
            </DialogTitle>
          </DialogHeader>
          <PipelineConfigCard onClose={() => setShowPipelineConfig(false)} />
        </DialogContent>
      </Dialog>
      <HelpGuideDialog {...{
  expandedSections, setExpandedSections, setShowHelpGuide, showHelpGuide,
      }} />
    </div>
  );
}
